import React, { useRef, useState } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import useMedia from 'components/custom-hooks/useMedia'
import Typo from 'constants/typography'
import { withI18next } from 'lib/i18n/withI18next'
import { hasLocizeTranslation } from 'lib/utils/locize'
import trustCopy from 'components/pdp/desktop-trust/copy'
import Coachmark from 'components/common/coachmark'
import { COACHMARK_POSITION } from 'components/common/coachmark/const'
import { saveClosedCoachMarkToStorage } from 'pages/product/utils'
import FindThisItemAtStoreCta from 'components/pdp/interactions/find-this-item-at-store-cta'
import Badge from 'components/common/badge'
import { BADGE_COLOR } from 'components/common/badge/const'
import styles from './styles.scss'
import SelectLabelItem from './label-item'
import SelectorItem from './selector-item'
import { MARK_DATA, SELECTOR_DATA } from './const'
import SizeLable from './size-label'

const OptionsSelector = ({
  localWaitlists,
  onSelected,
  optionMessage,
  setOptionMessage,
  product,
  selectedOption,
  t,
  isPhone,
  isCoachMarkClosed,
  sizeGuideAction,
  hideStoreCta,
  showSelectedSize,
  simpleSizeLabel,
  language,
}) => {
  const sizeLabelRef = useRef(null)
  const phone = useMedia('(max-width: 767px)')
  const [openMark, setOpenMark] = useState(product?.is_new_size_method || false)
  const isShowOnPhone = phone || isPhone
  const isShowMark = openMark && !isCoachMarkClosed
  const isAddedProductMeasurement = selectedOption?.is_new_measurement_added
  const isUpdatedProductMeasurement = selectedOption?.is_measurement_updated

  if (!product?.attributes?.length) {
    return null
  }

  const clearCaptionSize = () => {
    setOpenMark(false)

    saveClosedCoachMarkToStorage('pdp_size_coach_mark')
  }
  const coachMarkContainerProps = {
    style: {
      left: 0,
      padding: '12px',
      top: '-48px',
    },
  }

  return (
    <React.Fragment>
      <style jsx>{styles}</style>
      <div className="size-container">
        {isShowMark && (
          <Coachmark
            onClose={clearCaptionSize}
            position={COACHMARK_POSITION.BOTTOM_LEFT}
            text={hasLocizeTranslation(
              t,
              MARK_DATA.markText.titleKey,
              MARK_DATA.markText.default,
            )}
            isOpen={isShowMark}
            customContainerProps={coachMarkContainerProps}
            disableLeaveListener
            disableEnterListener
            customChildrenRef={sizeLabelRef}
          />
        )}
        <span className="size-container__size-title-label">
          <span className={Typo.subtitle2} ref={sizeLabelRef}>
            {simpleSizeLabel || !isShowOnPhone
              ? trustCopy(t, 'SIZE', language)
              : hasLocizeTranslation(
                  t,
                  SELECTOR_DATA.selectSizeTitle.titleKey,
                  SELECTOR_DATA.selectSizeTitle.default,
                )}
            &nbsp;
            {!selectedOption && (
              <SizeLable
                language={language}
                product={product}
                simpleLabel={simpleSizeLabel || !isShowOnPhone}
              />
            )}
          </span>
          {selectedOption && (
            <SelectLabelItem
              isPhone={isPhone && !showSelectedSize}
              isComingSoon={product?.is_coming_soon}
              item={selectedOption}
              localWaitlists={localWaitlists}
            />
          )}
          {isAddedProductMeasurement && (
            <span style={{ marginLeft: '5px' }}>
              <Badge
                color={BADGE_COLOR.secondary}
                contentClassName={clsx(Typo.overline)}
                content={hasLocizeTranslation(t, 'BADGE_NEW', 'NEW')}
              />
            </span>
          )}
          {isUpdatedProductMeasurement && (
            <span style={{ marginLeft: '5px' }}>
              <Badge
                color={BADGE_COLOR.secondary}
                contentClassName={clsx(Typo.overline)}
                content={hasLocizeTranslation(t, 'PDP_SIZE_UPDATED', 'UPDATED')}
              />
            </span>
          )}
          {sizeGuideAction}
        </span>
        <div className="pdp__options-container">
          {isShowOnPhone ? (
            <div className="pdp__options-slides">
              <SelectorItem
                attributes={product.attributes}
                selectedOption={selectedOption}
                onClick={onSelected}
                isComingSoon={product?.is_coming_soon}
              />
            </div>
          ) : (
            <div>
              <SelectorItem
                attributes={product.attributes}
                selectedOption={selectedOption}
                onClick={onSelected}
                isComingSoon={product?.is_coming_soon}
              />
            </div>
          )}
          {!hideStoreCta && (
            <FindThisItemAtStoreCta
              product={product}
              selectedOption={selectedOption}
              setOptionMessage={setOptionMessage}
            />
          )}
          {!!optionMessage && (
            <span className={clsx('pdp__options-message', Typo.caption)}>
              {optionMessage}
            </span>
          )}
        </div>
      </div>
    </React.Fragment>
  )
}

OptionsSelector.defaultProps = {
  language: 'en',
  simpleSizeLabel: false,
  selectedOption: null,
  optionMessage: '',
  isCoachMarkClosed: false,
  sizeGuideAction: null,
  hideStoreCta: false,
  showSelectedSize: false,
}

OptionsSelector.propTypes = {
  language: PropTypes.string,
  simpleSizeLabel: PropTypes.bool,
  sizeGuideAction: PropTypes.node,
  hideStoreCta: PropTypes.bool,
  showSelectedSize: PropTypes.bool,
  // component props
  localWaitlists: PropTypes.shape({}).isRequired,
  onSelected: PropTypes.func.isRequired,
  optionMessage: PropTypes.string,
  setOptionMessage: PropTypes.func.isRequired,
  product: PropTypes.shape({
    attributes: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    id_product: PropTypes.number.isRequired,
    is_new_size_method: PropTypes.bool,
    is_coming_soon: PropTypes.bool,
  }).isRequired,
  selectedOption: PropTypes.shape({
    id_product_attribute: PropTypes.number,
    size: PropTypes.string,
    is_measurement_updated: PropTypes.bool,
    is_new_measurement_added: PropTypes.bool,
  }),
  t: PropTypes.func.isRequired,
  isPhone: PropTypes.bool.isRequired,
  isCoachMarkClosed: PropTypes.bool,
}

export default withI18next()(OptionsSelector)
